import { Link2, Workflow, TrendingUp } from "lucide-react";
import { Reveal } from "./Reveal";

const PRINCIPLES = [
  {
    icon: Link2,
    title: "Connect what you have.",
    desc: "Work with the tools and systems you already use.",
  },
  {
    icon: Workflow,
    title: "Automate what slows you down.",
    desc: "We handle the repetitive work so you can focus on what matters.",
  },
  {
    icon: TrendingUp,
    title: "Grow with confidence.",
    desc: "Scale your operations without adding complexity.",
  },
];

const STARS = [
  { left: "12%", top: "18%" }, { left: "22%", top: "52%" }, { left: "8%", top: "70%" },
  { left: "88%", top: "22%" }, { left: "78%", top: "58%" }, { left: "92%", top: "66%" },
  { left: "16%", top: "86%" }, { left: "84%", top: "88%" },
];

export const AboutNova = () => (
  <section
    id="about"
    data-testid="about-section"
    className="relative scroll-mt-36 bg-[#060D24] text-white overflow-hidden"
  >
    <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
      <div className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full bg-nova-blue/[0.13] blur-[110px] animate-breathe" />
      <svg className="absolute left-1/2 top-[40%] -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] opacity-70" viewBox="0 0 900 900" fill="none">
        {[180, 280, 390].map((r) => (
          <circle key={r} cx="450" cy="450" r={r} stroke="rgba(96,165,250,0.10)" strokeWidth="1" />
        ))}
      </svg>
      {STARS.map((s, i) => (
        <span
          key={i}
          className="absolute w-1 h-1 rounded-full bg-[#60A5FA] animate-breathe"
          style={{ left: s.left, top: s.top, animationDelay: `${i * 1.1}s` }}
        />
      ))}
      <svg className="absolute bottom-[16%] left-0 w-full h-[140px] opacity-60" viewBox="0 0 1440 140" fill="none" preserveAspectRatio="none">
        <path d="M0 130 Q 720 -20 1440 130" stroke="rgba(96,165,250,0.28)" strokeWidth="1" />
      </svg>
    </div>

    <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-36">
      <Reveal>
        <div className="flex items-stretch gap-5 lg:gap-6">
          <div className="shrink-0">
            <span
              data-testid="chapter-07-number"
              className="text-5xl lg:text-6xl font-extrabold tracking-tighter text-nova-blue leading-none"
            >
              07
            </span>
            <span className="block mt-3 text-[11px] font-bold tracking-[0.28em] uppercase text-white/70">
              About Nova
            </span>
          </div>
          <span className="w-px bg-white/15 self-stretch" aria-hidden="true" />
        </div>
      </Reveal>

      <div className="mt-16 lg:mt-20 text-center max-w-3xl mx-auto">
        <Reveal delay={0.08}>
          <p className="flex items-center justify-center gap-5" data-testid="about-eyebrow">
            <span className="h-px w-10 bg-nova-blue/50" aria-hidden="true" />
            <span className="text-[11px] font-bold tracking-[0.34em] uppercase text-nova-blue">
              A New Way Forward.
            </span>
            <span className="h-px w-10 bg-nova-blue/50" aria-hidden="true" />
          </p>
        </Reveal>

        <Reveal delay={0.16}>
          <h2
            data-testid="about-headline"
            className="mt-7 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[1.05]"
          >
            <span className="text-white">Less Busywork.</span>
            <br />
            <span className="text-nova-blue">More Business.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.24}>
          <p className="mt-6 text-base lg:text-lg text-slate-300 leading-relaxed max-w-xl mx-auto">
            Bring your operations together and let Nova handle the repetitive
            work behind the scenes.
          </p>
        </Reveal>

        <Reveal delay={0.3}>
          <div className="relative mt-12 lg:mt-14 flex justify-center">
            <div
              aria-hidden="true"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full bg-nova-blue/20 blur-3xl animate-breathe"
            />
            <img
              src="/nova-icon-official.png"
              alt="Nova Opsis"
              data-testid="about-nova-logo"
              className="relative w-36 lg:w-44 select-none"
              draggable={false}
            />
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.36} className="mt-16 lg:mt-20">
        <div className="relative">
          <svg
            aria-hidden="true"
            className="hidden lg:block absolute -top-12 inset-x-0 w-full h-[110px] pointer-events-none"
            viewBox="0 0 1200 110"
            fill="none"
            preserveAspectRatio="none"
          >
            <path d="M0 100 Q 600 -25 1200 100" stroke="rgba(96,165,250,0.35)" strokeWidth="1" />
            <circle cx="14" cy="95" r="4" fill="#2563EB" className="animate-breathe" />
            <circle cx="1186" cy="95" r="4" fill="#2563EB" className="animate-breathe" style={{ animationDelay: "1.6s" }} />
          </svg>

          <div className="relative grid md:grid-cols-3 gap-12 md:gap-8 md:divide-x md:divide-white/10 max-w-5xl mx-auto">
            {PRINCIPLES.map((p) => (
              <div key={p.title} data-testid={`principle-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex flex-col items-center text-center px-2 md:px-8">
                <span className="w-16 h-16 rounded-full border border-nova-blue/40 bg-white/[0.02] flex items-center justify-center shadow-[0_0_30px_rgb(37,99,235,0.15)] shrink-0">
                  <p.icon size={24} strokeWidth={1.5} className="text-[#93C5FD]" />
                </span>
                <h3 className="mt-6 text-lg font-bold tracking-tight text-white [overflow-wrap:anywhere]">
                  {p.title}
                </h3>
                <p className="mt-2.5 text-sm text-slate-400 leading-relaxed max-w-[280px] [overflow-wrap:anywhere]">
                  {p.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
